import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { productService, orderService } from '../../services/firebaseService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Import hooks
import { useCartOperations } from './hooks/useCartOperations';
import { useOrderFlow } from './hooks/useOrderFlow';
import { useGiftSelection } from './hooks/useGiftSelection';
import { usePaymentPlan } from './hooks/usePaymentPlan';
import { useProductFiltering } from './hooks/useProductFiltering';
import { useUSPExpansion } from './hooks/useUSPExpansion';
import { useCustomerForm } from './hooks/useCustomerForm';
import { useOrderEffects } from './hooks/useOrderEffects';

// Import components
import { OrderCountryStep } from './components/OrderCountryStep';
import { OrderProductsStep } from './components/OrderProductsStep';
import { OrderCartStep } from './components/OrderCartStep';
import { OrderCustomerStep } from './components/OrderCustomerStep';
import { OrderSummaryStep } from './components/OrderSummaryStep';
import { OrderSuccessStep } from './components/OrderSuccessStep';

// Import services
import { createOrder } from './services/orderCreationService';

// Import utils
import { formatCurrency } from './utils/formatting';

const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const REGION = 'europe-west3';

export default function OrdersPage() {
  const [products, setProducts] = useState({});
  const [productsArray, setProductsArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [brokenImages, setBrokenImages] = useState({});
  const [customerData, setCustomerData] = useState(null);
  const [selectedProductForDetails, setSelectedProductForDetails] = useState(null);

  const { currentAgent } = useAuth();
  const { demoData } = useCart();
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [checkingUpgrade, setCheckingUpgrade] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Use custom hooks
  const cartOperations = useCartOperations();
  const orderFlow = useOrderFlow();
  const paymentPlan = usePaymentPlan(cartOperations.getDiscountedCartTotal);
  const productFiltering = useProductFiltering(products, upgradeInfo);
  const uspExpansion = useUSPExpansion();
  const customerForm = useCustomerForm(orderFlow.setStep, setCustomerData);
  
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      name: demoData?.customer?.name || '',
      email: demoData?.customer?.email || '',
      phone: demoData?.customer?.mobile || '',
      billingAddress: '',
      shippingAddress: '',
      sameAsBilling: true,
      demoBookingId: demoData?.demoId || ''
    }
  });

  const giftSelection = useGiftSelection(
    productsArray, 
    cartOperations.paidItems, 
    cartOperations.cart, 
    cartOperations.addGift, 
    cartOperations.removeFromCart,
    () => {},
    upgradeInfo
  );

  // Use effects
  useOrderEffects(orderFlow.step, watch('sameAsBilling'), watch('billingAddress'), setValue);

  // Pre-fill country if demoData has it
  useEffect(() => {
    if (demoData?.customer?.country_iso && !cartOperations.selectedCountry?.id) {
      const countryCode = demoData.customer.country_iso;
      const foundCountry = cartOperations.COUNTRIES.find(c => c.id === countryCode);
      if (foundCountry) {
        cartOperations.updateCountry(foundCountry);
        if (orderFlow.step === 'country') {
          orderFlow.setStep('products');
        }
      }
    }
  }, [demoData, cartOperations.COUNTRIES, orderFlow.step]);

  // Load products and enforce demo link
  useEffect(() => {
    if (!demoData || !demoData.demoId) {
      navigate('/dashboard');
      return;
    }
    loadProducts();
  }, [demoData, navigate]);

  useEffect(() => {
    if (demoData?.upgradeInfo) {
      setUpgradeInfo(demoData.upgradeInfo);
      if (demoData.customer) {
        setValue('name', demoData.customer.name || '');
        setValue('email', demoData.customer.email || '');
        setValue('phone', demoData.customer.mobile || '');
      }
    }
  }, [demoData, setValue]);

  const handleCheckUpgrade = async (email, mobile) => {
    try {
      setCheckingUpgrade(true);
      const result = await orderService.findCustomerUpgradeEligibility(email, mobile);
      setUpgradeInfo(result);
      if (result.eligible) {
        setShowUpgradeModal(false);
      }
    } catch (err) {
      console.error('Error checking upgrade:', err);
    } finally {
      setCheckingUpgrade(false);
    }
  };

  const handleAddToCart = (product, quantity) => {
    let price = product.prices?.[cartOperations.selectedCurrency] || product.prices?.USD || 0;
    let isUpgrade = false;
    let upgradeFrom = null;
    let upgradeFromOrder = null;
    let upgradeFromOrderId = null;
    let lowerSetSku = null;

    if (upgradeInfo?.eligible && product.BrowsingGroup === 'SET') {
      const discount = upgradeInfo.highestSet.priceLocal || 0;
      price = Math.max(0, price - discount);
      isUpgrade = true;
      upgradeFrom = upgradeInfo.highestSet.sku;
      upgradeFromOrder = upgradeInfo.highestSet.orderCode;
      upgradeFromOrderId = upgradeInfo.highestSet.orderId;
      lowerSetSku = upgradeInfo.lowerSetSku;
    }

    cartOperations.addToCart({
      ...product,
      price: price,
      isUpgrade: isUpgrade,
      upgradeFrom: upgradeFrom,
      upgradeFromOrder: upgradeFromOrder,
      upgradeFromOrderId: upgradeFromOrderId,
      lowerSetSku: lowerSetSku
    }, quantity);
  };

  // Check gift eligibility when cart step is active
  useEffect(() => {
    if (orderFlow.step === 'cart' && cartOperations.paidItems.length > 0) {
      giftSelection.checkGiftEligibility();
    }
  }, [cartOperations.paidItems, orderFlow.step]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const productsData = await productService.getProducts();
      setProducts(productsData);
      setProductsArray(Object.values(productsData));
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFormattedCartTotal = () => {
    return formatCurrency(cartOperations.getDiscountedCartTotal(), cartOperations.selectedCurrency);
  };

  const getFormattedSubtotal = () => {
    return formatCurrency(cartOperations.getCartTotal(), cartOperations.selectedCurrency);
  };

  const handleCreateOrder = async () => {
    const result = await createOrder({
      cart: cartOperations.cart,
      paidItems: cartOperations.paidItems,
      giftItems: cartOperations.giftItems,
      customerData,
      selectedCurrency: cartOperations.selectedCurrency,
      selectedCountry: cartOperations.selectedCountry,
      checkoutPaymentPeriod: paymentPlan.checkoutPaymentPeriod,
      installmentFrequency: paymentPlan.installmentFrequency,
      installmentOverride: paymentPlan.installmentOverride,
      numberOfInstallments: paymentPlan.numberOfInstallments,
      getSelectedPaymentPeriod: paymentPlan.getSelectedPaymentPeriod,
      orderPath: orderFlow.orderPath,
      currentAgent,
      demoData,
      COUNTRIES: cartOperations.COUNTRIES,
      getCartTotal: cartOperations.getCartTotal,
      getDiscountedCartTotal: cartOperations.getDiscountedCartTotal,
      discountAmt: cartOperations.discountAmt,
      clearCart: cartOperations.clearCart,
      setCustomerData,
      reset,
      setStep: orderFlow.setStep,
      setLoading
    });

    if (result?.success) {
      if (orderFlow.orderPath === 'draft') {
        alert(`Draft created successfully! Draft number: ${result.draftOrderNumber}`);
        navigate('/draft-orders');
      } else {
        alert(`Order created successfully! Order ID: ${result.orderNumber}`);
        navigate('/my-orders');
      }
      cartOperations.clearCart();
      setCustomerData(null);
      reset();
    }
  };

  if (loading && Object.keys(products).length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Render appropriate step
  switch (orderFlow.step) {
    case 'country':
      return (
        <OrderCountryStep
          selectedCountry={cartOperations.selectedCountry}
          updateCountry={cartOperations.updateCountry}
          setStep={orderFlow.setStep}
          COUNTRIES={cartOperations.COUNTRIES}
        />
      );
    
    case 'products':
      return (
        <OrderProductsStep
          filteredProducts={productFiltering.filteredProducts}
          products={products}
          selectedCurrency={cartOperations.selectedCurrency}
          selectedCountry={cartOperations.selectedCountry}
          addToCart={handleAddToCart}
          cart={cartOperations.cart}
          getCartCount={cartOperations.getCartCount}
          setStep={orderFlow.setStep}
          brokenImages={brokenImages}
          setBrokenImages={setBrokenImages}
          expandedUSPProducts={uspExpansion.expandedUSPProducts}
          toggleUSPExpansion={uspExpansion.toggleUSPExpansion}
          selectedProductForDetails={selectedProductForDetails}
          setSelectedProductForDetails={setSelectedProductForDetails}
          browsingGroups={productFiltering.browsingGroups}
          selectedBrowsingGroup={productFiltering.selectedBrowsingGroup}
          setSelectedBrowsingGroup={productFiltering.setSelectedBrowsingGroup}
          searchTerm={productFiltering.searchTerm}
          setSearchTerm={productFiltering.setSearchTerm}
          PROJECT_ID={PROJECT_ID}
          REGION={REGION}
          upgradeInfo={upgradeInfo}
          setUpgradeInfo={setUpgradeInfo}
          showUpgradeModal={showUpgradeModal}
          setShowUpgradeModal={setShowUpgradeModal}
          checkingUpgrade={checkingUpgrade}
          handleCheckUpgrade={handleCheckUpgrade}
        />
      );
    
    case 'cart':
      const canApplyDiscount = 
        currentAgent?.role === 'teamLeader' || 
        currentAgent?.role === 'admin';
      
      return (
        <OrderCartStep
          cart={cartOperations.cart}
          paidItems={cartOperations.paidItems}
          giftItems={cartOperations.giftItems}
          selectedCurrency={cartOperations.selectedCurrency}
          getCartTotal={cartOperations.getCartTotal}
          getDiscountedCartTotal={cartOperations.getDiscountedCartTotal}
          discountAmt={cartOperations.discountAmt}
          setDiscountAmt={cartOperations.setDiscountAmt}
          getFormattedCartTotal={getFormattedCartTotal}
          getFormattedSubtotal={getFormattedSubtotal}
          updateQuantity={cartOperations.updateQuantity}
          removeFromCart={cartOperations.removeFromCart}
          removeGift={cartOperations.removeGift}
          setStep={orderFlow.setStep}
          brokenImages={brokenImages}
          setBrokenImages={setBrokenImages}
          showGiftModal={giftSelection.showGiftModal}
          eligibleProducts={giftSelection.eligibleProducts}
          selectedProductForGifts={giftSelection.selectedProductForGifts}
          handleGiftSelectionComplete={giftSelection.handleGiftSelectionComplete}
          closeGiftModal={giftSelection.closeGiftModal}
          openGiftModalForProduct={giftSelection.openGiftModalForProduct}
          checkoutPaymentPeriod={paymentPlan.checkoutPaymentPeriod}
          setCheckoutPaymentPeriod={paymentPlan.setCheckoutPaymentPeriod}
          installmentFrequency={paymentPlan.installmentFrequency}
          setInstallmentFrequency={paymentPlan.setInstallmentFrequency}
          numberOfInstallments={paymentPlan.numberOfInstallments}
          setNumberOfInstallments={paymentPlan.setNumberOfInstallments}
          installmentOverride={paymentPlan.installmentOverride}
          setInstallmentOverride={paymentPlan.setInstallmentOverride}
          getSelectedPaymentPeriod={paymentPlan.getSelectedPaymentPeriod}
          PROJECT_ID={PROJECT_ID}
          REGION={REGION}
          canApplyDiscount={canApplyDiscount}
        />
      );
    
    case 'customer':
      return (
        <OrderCustomerStep
          register={register}
          errors={errors}
          handleSubmit={handleSubmit}
          onCustomerSubmit={customerForm.onCustomerSubmit}
          paymentMethod={customerForm.paymentMethod}
          setPaymentMethod={customerForm.setPaymentMethod}
          paymentReference={customerForm.paymentReference}
          setPaymentReference={customerForm.setPaymentReference}
          amountPaidUSD={customerForm.amountPaidUSD}
          setAmountPaidUSD={customerForm.setAmountPaidUSD}
          amountPaidLocal={customerForm.amountPaidLocal}
          setAmountPaidLocal={customerForm.setAmountPaidLocal}
          paymentStatus={customerForm.paymentStatus}
          setPaymentStatus={customerForm.setPaymentStatus}
          watchSameAsBilling={watch('sameAsBilling')}
          setStep={orderFlow.setStep}
          demoData={demoData}
        />
      );
    
    case 'summary':
      return (
        <OrderSummaryStep
          customerData={customerData}
          paidItems={cartOperations.paidItems}
          giftItems={cartOperations.giftItems}
          getFormattedCartTotal={getFormattedCartTotal}
          getFormattedSubtotal={getFormattedSubtotal}
          selectedCurrency={cartOperations.selectedCurrency}
          getCartTotal={cartOperations.getCartTotal}
          getDiscountedCartTotal={cartOperations.getDiscountedCartTotal}
          discountAmt={cartOperations.discountAmt}
          checkoutPaymentPeriod={paymentPlan.checkoutPaymentPeriod}
          installmentOverride={paymentPlan.installmentOverride}
          numberOfInstallments={paymentPlan.numberOfInstallments}
          getSelectedPaymentPeriod={paymentPlan.getSelectedPaymentPeriod}
          orderPath={orderFlow.orderPath}
          setOrderPath={orderFlow.setOrderPath}
          loading={loading}
          setStep={orderFlow.setStep}
          createOrder={handleCreateOrder}
          demoData={demoData}
        />
      );
    
    case 'success':
      return <OrderSuccessStep setStep={orderFlow.setStep} />;
    
    default:
      return null;
  }
}
